import { useRef, useState } from 'react';
import {
  Button,
  Calendar,
  Fieldset,
  Grid,
  GridCol,
  Heading,
  Input,
  Popup,
  PopupSection,
  Tooltip,
} from '@flixbus/honeycomb-react';
import { Icon, IconCalendar } from '@flixbus/honeycomb-icons-react';

import { useBatchUpdate } from '../../context/VoucherBatchUpdateProvider';
import { useNotification } from '../Notifications/Context';

import { fetchVoucherBatch } from '../../apis';
import { DateTime } from 'luxon';
import { toISODate } from '../../helpers/dateTime';

import styles from './voucher-actions.module.scss';

const ChangeExpiration = () => {
  const [popupActive, setPopupActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const { pushToast } = useNotification();
  const { payload, handleUpdate, clearSelection, refetch } = useBatchUpdate();
  const targetRef = useRef(null);

  const [localValidSince, setLocalValidSince] = useState(
    payload.batches[0]?.query?.validityInterval?.validSince || '',
  );
  const [localValidUntil, setLocalValidUntil] = useState(
    payload.batches[0]?.query?.validityInterval?.validUntil || '',
  );

  const [calendarOpenSince, setCalendarOpenSince] = useState(false);
  const [calendarOpenUntil, setCalendarOpenUntil] = useState(false);

  const vouchersCount = payload.batches[0].ids.length;
  const today = new Date().toISOString().split('T')[0];

  const handleDateChange = (prop: 'validSince' | 'validUntil', value: string) => {
    if (!value) return;

    if (prop === 'validSince') {
      if (localValidUntil && DateTime.fromISO(value) >= DateTime.fromISO(localValidUntil)) {
        return;
      }
      setLocalValidSince(value);
    } else {
      if (DateTime.fromISO(value) <= DateTime.fromISO(localValidSince)) {
        return;
      }
      setLocalValidUntil(value);
    }

    handleUpdate({
      prop: 'validityInterval',
      value: {
        ...payload.batches[0]?.query?.validityInterval,
        [prop]: toISODate(DateTime.fromISO(value)),
      },
    });
  };

  const onSubmit = () => {
    setPopupActive(true);
  };

  const onConfirm = async () => {
    setLoading(true);
    try {
      const query = { ...payload };
      await fetchVoucherBatch(query);
      setPopupActive(false);
      pushToast({
        message: `${vouchersCount} voucher(s) were successfully changed`,
        status: 'success',
      });

      setTimeout(() => {
        refetch();
        clearSelection();
      }, 1000);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'An unknown error occurred';
      const detail = JSON.parse(message)?.detail || message;

      setPopupActive(false);
      pushToast({
        message: detail,
        status: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const disablePastDates = (date: Date) => {
    const min = new Date(today);
    min.setHours(0, 0, 0, 0);
    return date < min;
  };

  const calendarEndDate = new Date();
  calendarEndDate.setFullYear(calendarEndDate.getFullYear() + 5);

  const sinceDate = localValidSince ? DateTime.fromISO(localValidSince) : null;
  const untilDate = localValidUntil ? DateTime.fromISO(localValidUntil) : null;

  return (
    <>
      <div>
        <Tooltip
          id='language-selection'
          size='large'
          position='bottom'
          alignment='start'
          content={
            <Fieldset itemSpacing='half'>
              <Grid>
                <GridCol>
                  <div className={styles.calendar}>
                    <Input
                      id='date-field-validSince'
                      label='Voucher valid from'
                      type='text'
                      readOnly
                      value={sinceDate?.isValid ? sinceDate.toFormat('dd.MM.yyyy') : ''}
                      placeholder='DD.MM.YYYY'
                      onClick={() => {
                        setCalendarOpenSince(prev => !prev);
                        setCalendarOpenUntil(false);
                      }}
                      iconLeft={
                        <Button
                          aria-label='Open calendar'
                          onClick={() => {
                            setCalendarOpenSince(prev => !prev);
                            setCalendarOpenUntil(false);
                          }}
                        >
                          <Icon appearance='primary' InlineIcon={IconCalendar} />
                        </Button>
                      }
                    />
                    {calendarOpenSince && (
                      <Calendar
                        id='calendar-validSince'
                        appearance='compact'
                        startDate={new Date(today)}
                        endDate={calendarEndDate}
                        selected={sinceDate?.isValid ? sinceDate.toJSDate() : undefined}
                        showCurrentDate
                        handleSelect={date => {
                          handleDateChange(
                            'validSince',
                            DateTime.fromJSDate(date).toFormat('yyyy-MM-dd'),
                          );
                          setCalendarOpenSince(false);
                        }}
                        disableDayCondition={disablePastDates}
                      />
                    )}
                  </div>
                </GridCol>
                <GridCol>
                  <div className={styles.calendar}>
                    <Input
                      id='date-field-validUntil'
                      label='Voucher valid until'
                      type='text'
                      readOnly
                      value={untilDate?.isValid ? untilDate.toFormat('dd.MM.yyyy') : ''}
                      placeholder='DD.MM.YYYY'
                      onClick={() => {
                        setCalendarOpenUntil(prev => !prev);
                        setCalendarOpenSince(false);
                      }}
                      iconLeft={
                        <Button
                          aria-label='Open calendar'
                          onClick={() => {
                            setCalendarOpenUntil(prev => !prev);
                            setCalendarOpenSince(false);
                          }}
                        >
                          <Icon appearance='primary' InlineIcon={IconCalendar} />
                        </Button>
                      }
                    />
                    {calendarOpenUntil && (
                      <Calendar
                        id='calendar-validUntil'
                        appearance='compact'
                        startDate={new Date(today)}
                        endDate={calendarEndDate}
                        selected={untilDate?.isValid ? untilDate.toJSDate() : undefined}
                        showCurrentDate
                        handleSelect={date => {
                          handleDateChange(
                            'validUntil',
                            DateTime.fromJSDate(date).toFormat('yyyy-MM-dd'),
                          );
                          setCalendarOpenUntil(false);
                        }}
                        disableDayCondition={disablePastDates}
                      />
                    )}
                  </div>
                </GridCol>
              </Grid>

              <Button
                onClick={onSubmit}
                appearance='primary'
                disabled={!localValidSince || !localValidUntil}
              >
                Submit
              </Button>
            </Fieldset>
          }
        >
          <Button appearance='link' disabled={vouchersCount === 0}>
            <Icon InlineIcon={IconCalendar} />
            Change expiration
          </Button>
        </Tooltip>
      </div>

      <Popup
        active={popupActive}
        onOverlayClick={() => setPopupActive(false)}
        targetRef={targetRef}
      >
        <PopupSection type='icon'>
          <Icon InlineIcon={IconCalendar} size={12} style={{ fill: 'var(--hcr-warning-color)' }} />
        </PopupSection>
        <PopupSection type='title'>
          <Heading size={2} sectionHeader flushSpace>
            Change Expiration Date?
          </Heading>
        </PopupSection>
        <PopupSection type='content'>
          You are about to change the expiration date for <b>{vouchersCount}</b> vouchers from{' '}
          <b>{DateTime.fromISO(localValidSince).toFormat('dd.MM.yyyy')}</b> to{' '}
          <b>{DateTime.fromISO(localValidUntil).toFormat('dd.MM.yyyy')}</b>. Make sure you selected
          the correct vouchers and the changes are correct before continuing.
        </PopupSection>
        <PopupSection type='actions'>
          <Button onClick={() => setPopupActive(false)}>Go Back</Button>
          <Button appearance='primary' onClick={onConfirm} loading={loading}>
            Confirm
          </Button>
        </PopupSection>
      </Popup>
    </>
  );
};

export default ChangeExpiration;
